import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Grid,
  Card,
  CardMedia,
  Tabs,
  Tab,
  Typography,
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Button,
  ButtonBase,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ThumbUp from "@material-ui/icons/ThumbUp";
import ChatBubbleRounded from "@material-ui/icons/ChatBubbleOutline";
import NotInterested from "@material-ui/icons/NotInterested";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ReportIcon from "@material-ui/icons/Report";

import ProfileDetails from "../../api_calls/ProfileDetailsApi";
import { useAuth } from "../../context/AuthContext";
import { useWebSocket } from "../../context/WebSocketContext";
import { useCall } from "../../context/CallContext";
import Configs from "../../utils/Configs";

const AccountInterface = () => {
  const { userId } = useParams();
  const { user } = useAuth();
  const { sendMessage } = useWebSocket();
  const { actionStatus } = useCall();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [liked, setLiked] = useState(false);
  const [blocked, setBlocked] = useState(false);

  const [reportOpen, setReportOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");
  const [additionalDetails, setAdditionalDetails] = useState("");

  useEffect(() => {
    (async () => {
      if (!userId) {
        setError("No user ID provided");
        setLoading(false);
        return;
      }
      const data = await ProfileDetails.fetchProfileDetails(userId);
      if (!data) {
        setError("Failed to load profile details.");
      } else {
        console.log(data);
        setProfile(data);
        setLiked(!!data.isLikedByCurrentUser);
        setBlocked(!!data.isBlockedByCurrentUser);
      }
      setLoading(false);
    })();
  }, [userId]);

  useEffect(() => {
    if (
      !actionStatus ||
      actionStatus.peerId !== userId ||
      actionStatus.status !== "success"
    )
      return;
    switch (actionStatus.tag) {
      case "like":
        setLiked(true);
        break;
      case "unlike":
        setLiked(false);
        break;
      case "block":
        setBlocked(true);
        break;
      case "unblock":
        setBlocked(false);
        break;
      default:
        break;
    }
  }, [actionStatus, userId]);

  const handleLikeToggle = () => {
    sendMessage({
      user_id: user.user_id,
      tag: liked ? "unlike" : "like",
      payload: { peer_id: userId },
    });
  };

  const handleBlockToggle = () => {
    sendMessage({
      user_id: user.user_id,
      tag: blocked ? "unblock" : "block",
      payload: { peer_id: userId },
    });
  };

  const handleReportSubmit = () => {
    // 🔍 Log the three required details
    console.log("Submitting report with the following details:");
    console.log("Account Owner ID:", user.user_id);
    console.log("Profile ID:", userId);
    console.log("Selected Reason:", selectedReason);

    // ✅ Send report through WebSocket
    sendMessage({
      user_id: user.user_id, // reporter
      tag: "report",
      payload: {
        peer_id: userId, // reported profile
        reason: selectedReason,
        details: additionalDetails,
      },
    });

    // 🔄 Reset modal state
    setReportOpen(false);
    setSelectedReason("");
    setAdditionalDetails("");
  };

  if (loading)
    return <CircularProgress sx={{ display: "block", margin: "50px auto" }} />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box sx={{ mx: 1 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1,
          mb: 1,
        }}
      >
        <Typography fontWeight="bold">
          {profile.pseudo_name
            ? profile.pseudo_name.charAt(0).toUpperCase() +
              profile.pseudo_name.slice(1).toLowerCase()
            : "Unknown"}
          {" · "}
          {profile.age ? `${profile.age} yrs` : "Age unknown"}
        </Typography>
        {profile.country_iso_two && profile.location && (
          <>
            <img
              src={`${Configs.flagCdnBaseUrl}/${profile.country_iso_two.toLowerCase()}.png`}
              alt="flag"
              width="24"
              height="16"
              style={{ borderRadius: 2 }}
            />
            <Typography>
              {profile.location
                .replace(/^, /, "")
                .replace(/\b\w/g, (c) => c.toUpperCase())}
            </Typography>
          </>
        )}
      </Box>

      <Grid container spacing={2} sx={{ alignItems: "start" }}>
        {/* Left Panel */}
        <Grid item xs={12} md={3}>
          <Card sx={{ aspectRatio: "1", borderRadius: 2 }}>
            <CardMedia
              component="img"
              src={profile.profile_image || "https://via.placeholder.com/300"}
              alt="Profile"
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </Card>
          <Box sx={{ display: "flex", justifyContent: "space-around", mt: 1 }}>
            <ThumbButton
              active={liked}
              onClick={handleLikeToggle}
              Icon={ThumbUp}
              label={liked ? "Unlike" : "Like"}
              color={liked ? "error" : "default"}
            />
            <ThumbButton
              active={blocked}
              onClick={handleBlockToggle}
              Icon={NotInterested}
              label={blocked ? "Unblock" : "Block"}
              color={blocked ? "secondary" : "default"}
            />
            <ThumbButton
              active={false}
              onClick={() => setReportOpen(true)}
              Icon={ReportIcon}
              label="Report"
              color="warning"
            />
          </Box>
        </Grid>

        {/* Right Panel */}
        <Grid item xs={12} md={9}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="fullWidth"
            sx={{
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "flex-start",
              justifyContent: "flex-start",
              bgcolor: "transparent",
              mb: 2, // 👈 Adds space between tabs and the content
            }}
            TabIndicatorProps={{ style: { display: "none" } }}
          >
            {[
              { label: "Photo Gallery" },
              { label: `About ${profile.pseudo_name}` },
              { label: "Social Media" },
            ].map((tab, index) => (
              <Tab
                key={index}
                label={tab.label}
                sx={{
                  justifyContent: "flex-start",
                  textAlign: "left",
                  minWidth: 0,
                  width: "auto",
                  pl: 2,
                  pr: 2,
                  borderRadius: 2,
                  bgcolor: activeTab === index ? "#f9f9f9" : "transparent",
                  textTransform: "none",
                  fontWeight: activeTab === index ? "bold" : "normal",
                  "&:hover": {
                    bgcolor: "#f9f9f9",
                  },
                }}
              />
            ))}
          </Tabs>

          <Box sx={{ p: 2, bgcolor: "white", borderRadius: "0 0 8px 8px" }}>
            {activeTab === 0 && (
              <Box
                sx={{
                  display: "grid",
                  gap: 1,
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                    lg: "repeat(4, 1fr)",
                    xl: "repeat(5, 1fr)",
                  },
                }}
              >
                {profile.photos?.length > 0 ? (
                  profile.photos.map((photo, i) => (
                    <CardMedia
                      key={photo.id} // Ensure photo.id is unique
                      component="img"
                      src={photo.url}
                      alt={`Photo ${i + 1}`}
                      sx={{
                        width: "100%",
                        aspectRatio: "1",
                        objectFit: "cover",
                        borderRadius: 2,
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setCurrentImageIndex(i);
                        setLightboxOpen(true);
                      }}
                    />
                  ))
                ) : (
                  <Typography>No photos available</Typography>
                )}
              </Box>
            )}
            {activeTab === 1 && (
              <>
                <Typography fontWeight="bold">About Me</Typography>
                <Typography>
                  {profile.aboutMe || "No details available."}
                </Typography>
                <Typography sx={{ mt: 2 }} fontWeight="bold">
                  Interests & Preferences
                </Typography>
                {[
                  {
                    title: "Cultural Values",
                    items: profile.preferences?.cultural_values,
                  },
                  { title: "Hobbies", items: profile.preferences?.hobbies },
                  {
                    title: "Lifestyle Preferences",
                    items: profile.preferences?.lifestyle_preferences,
                  },
                  {
                    title: "Personality Traits",
                    items: profile.preferences?.personality_traits,
                  },
                  {
                    title: "Relationship Goals",
                    items: profile.preferences?.relationship_goals,
                  },
                ].map(({ title, items }, index) => (
                  <Box key={index} sx={{ mt: 2 }}>
                    <Typography fontWeight="bold">{title}</Typography>
                    {items?.length > 0 ? (
                      items.map((item, i) => (
                        <Box
                          key={i}
                          sx={{ display: "flex", alignItems: "center", mb: 1 }}
                        >
                          <CheckCircleOutlineIcon
                            sx={{ color: "#4caf50", mr: 1 }}
                          />
                          <Typography>{item.replace(/_/g, " ")}</Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography sx={{ fontStyle: "italic" }}>
                        (None was found)
                      </Typography>
                    )}
                  </Box>
                ))}
              </>
            )}
            {activeTab === 2 && (
              <>
                <Typography variant="h5" gutterBottom>
                  Social Media
                </Typography>
                {["facebook", "instagram", "twitter"].map((key) =>
                  profile.social_media?.[key] ? (
                    <Typography key={key}>
                      <a
                        href={profile.social_media[key]}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {key.charAt(0).toUpperCase() + key.slice(1)}
                      </a>
                    </Typography>
                  ) : null
                )}
              </>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Safety Tips */}
      <Box
        sx={{
          mt: 3,
          p: 2,
          bgcolor: "#f9f9f9",
          borderRadius: 2,
          border: "1px solid #ddd",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Safety Tips
        </Typography>
        <Box component="ul" sx={{ pl: 0, m: 0 }} style={{ listStyle: "none" }}>
          {[
            "Never share your passwords or personal information.",
            "Meet in public places for first meetings.",
            "Trust your instincts and report suspicious behavior.",
            "Keep conversations respectful and courteous.",
            "Use the block feature if you feel uncomfortable.",
          ].map((tip, index) => (
            <li
              key={index}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
                marginBottom: "16px",
              }}
            >
              <CheckCircleIcon style={{ color: "red", marginTop: "4px" }} />
              <span>{tip}</span>
            </li>
          ))}
        </Box>
      </Box>

      {/* Lightbox */}
      {lightboxOpen && (
        <Dialog fullScreen open onClose={() => setLightboxOpen(false)}>
          <Box sx={{ height: "100vh", bgcolor: "black", position: "relative" }}>
            <IconButton
              onClick={() => setLightboxOpen(false)}
              sx={{ position: "absolute", top: 20, right: 20, color: "white" }}
            >
              <CloseIcon />
            </IconButton>
            <IconButton
              onClick={() =>
                setCurrentImageIndex(
                  (i) => (i - 1 + profile.photos.length) % profile.photos.length
                )
              }
              sx={{
                position: "absolute",
                top: "50%",
                left: 20,
                transform: "translateY(-50%)",
                color: "white",
                zIndex: 1300,
              }}
            >
              <ArrowBackIosNewIcon />
            </IconButton>
            <img
              src={profile.photos[currentImageIndex]?.url}
              alt="Full View"
              style={{
                maxHeight: "80vh",
                maxWidth: "90vw",
                margin: "auto",
                display: "block",
                borderRadius: 10,
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            />

            <IconButton
              onClick={() =>
                setCurrentImageIndex((i) => (i + 1) % profile.photos.length)
              }
              sx={{
                position: "absolute",
                top: "50%",
                right: 20,
                transform: "translateY(-50%)",
                color: "white",
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </Box>
        </Dialog>
      )}

      {/* Report Modal */}
      <Dialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Report User
          </Typography>
          <Typography variant="body2" gutterBottom>
            Why are you reporting this profile?
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            {[
              "Inappropriate Content",
              "Spam or Scam",
              "Fake Profile",
              "Harassment",
              "Other",
            ].map((reason) => (
              <Box
                key={reason}
                onClick={() => setSelectedReason(reason)}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor:
                    selectedReason === reason ? "primary.main" : "#ccc",
                  bgcolor: selectedReason === reason ? "#f0f8ff" : "white",
                  cursor: "pointer",
                }}
              >
                <Typography>{reason}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" gutterBottom>
              Additional Details (optional)
            </Typography>
            <textarea
              rows={4}
              value={additionalDetails}
              onChange={(e) => setAdditionalDetails(e.target.value)}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "8px",
                border: "1px solid #ccc",
              }}
            />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Button onClick={() => setReportOpen(false)} sx={{ mr: 2 }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={!selectedReason}
              onClick={handleReportSubmit}
            >
              Submit Report
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default AccountInterface;

// --- Helper Component ---
const ThumbButton = ({ active, onClick, Icon, label, color }) => (
  <ButtonBase
    onClick={onClick}
    sx={{ flexDirection: "column", display: "flex", alignItems: "center" }}
  >
    <Icon style={{ color: color === "default" ? undefined : color }} />
    <Typography variant="caption" sx={{ mt: 0.5 }}>
      {label}
    </Typography>
  </ButtonBase>
);
